import { Link } from 'react-router-dom';
import { ChevronRight, Headphones, MessageCircle, Phone } from 'lucide-react';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { usePatientSupportChat } from '../../lib/patientSupportChat';
import { usePatientUnreadMessages } from '../../hooks/usePatientUnreadMessages';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { SupportChatButton, SupportSelfServe } from '../../components/dtp/PatientSupport';
import { usePatientAvaStatus } from '../../lib/patientAva';
import { AvaMark } from '../../components/dtp/AvaMark';

/**
 * Help: support chat first (orders, shipping, payments, the account), the
 * clinic's care team second (clinical questions), then self-serve links.
 */
export function DtpSupport() {
  const { physicianId } = useDtpParams();
  const { status } = usePatientSupportChat();
  const impersonated = useDtpAuthStore(s => s.impersonated);
  const careUnread = usePatientUnreadMessages();
  const chatOffered = status === 'loading' || status === 'ready';
  const ava = usePatientAvaStatus();

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="portal-display text-[36px] font-bold leading-none sm:text-[44px]">Help & support</h1>
      <p className="mt-3 text-[17px] text-[var(--portal-muted)]">We’re here for anything about your orders, payments or account.</p>

      <div className="mt-8 space-y-4">
      {/* Without support chat on this deploy, the page leads with the care team. */}
      {chatOffered && <section aria-labelledby="support-chat" className="relative overflow-hidden rounded-[24px] bg-[var(--portal-primary)] p-6 text-[var(--portal-primary-fg)] shadow-[0_18px_40px_-28px_rgba(36,26,21,0.6)] sm:p-8">
        <span aria-hidden="true" className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-white/[0.06]" />
        <div className="relative flex items-center gap-3">
          <span aria-hidden="true" className="grid h-10 w-10 place-items-center rounded-xl bg-white/10"><Headphones className="h-5 w-5" /></span>
          <h2 id="support-chat" className="portal-display text-2xl font-bold text-inherit">Chat with support</h2>
        </div>
        {impersonated ? (
          <p className="relative mt-4 text-[15px] opacity-90">Support chat is only available to the patient.</p>
        ) : <>
          <p className="relative mt-4 max-w-lg text-[15px] opacity-90">Order status and shipping, payments and receipts, auto-refills, signing in, or anything that isn’t working. Our team answers in this chat.</p>
          <div className="relative mt-6"><SupportChatButton variant="inverse" label="Start a chat" placement="help_page" /></div>
        </>}
      </section>}

      {ava.available && <Link to={dtpPath(physicianId, '/assistant')} className="portal-card group flex items-center justify-between gap-4 p-5 transition hover:-translate-y-0.5 motion-reduce:hover:translate-y-0 sm:p-6">
        <span className="flex min-w-0 items-center gap-4">
          <AvaMark size={40} />
          <span className="min-w-0">
            <span className="portal-display block font-semibold">Ask Ava for a quick answer</span>
            <span className="mt-0.5 block text-sm text-[var(--portal-muted)]">Our AI assistant can look up your orders, refills, results and payments, any time.</span>
          </span>
        </span>
        <ChevronRight aria-hidden="true" className="h-5 w-5 shrink-0 text-[var(--portal-muted)] transition group-hover:translate-x-0.5" />
      </Link>}

      <Link to={dtpPath(physicianId, '/messages')} className="portal-card group flex items-center justify-between gap-4 p-5 transition hover:-translate-y-0.5 motion-reduce:hover:translate-y-0 sm:p-6">
        <span className="flex min-w-0 items-center gap-4">
          <span aria-hidden="true" className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--portal-tint-1)]"><MessageCircle className="h-5 w-5" /></span>
          <span className="min-w-0">
            <span className="flex items-center gap-2"><span className="portal-display font-semibold">Medical question? Message your care team</span>{careUnread > 0 && <span className="rounded-full bg-[var(--portal-accent)] px-2 py-0.5 text-xs font-bold text-[var(--portal-accent-fg)]">{careUnread} new</span>}</span>
            <span className="mt-0.5 block text-sm text-[var(--portal-muted)]">Side effects, dosing, prescriptions and refills go to your clinic’s clinicians.</span>
          </span>
        </span>
        <ChevronRight aria-hidden="true" className="h-5 w-5 shrink-0 text-[var(--portal-muted)] transition group-hover:translate-x-0.5" />
      </Link>
      </div>

      <h2 className="mt-10 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--portal-muted)]">Find it yourself</h2>
      <div className="mt-3"><SupportSelfServe /></div>

      <p className="mt-8 flex items-start gap-2 text-sm text-[var(--portal-muted)]"><Phone aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />In an emergency, call 911 or go to the nearest emergency room. {chatOffered ? 'Support chat and messages aren’t' : 'Messages aren’t'} monitored around the clock.</p>
    </div>
  );
}

export default DtpSupport;
